import React, { useState, useCallback } from 'react';
import Title from './title';
import moment from 'moment';

type SortType = 'string' | 'number' | 'date';
type SortDirection = 'asc' | 'desc' | null;

interface SortableColumn {
  index: number;
  key: string;
  type: SortType;
  dateFormat?: string;
  accessor?: (item: any) => any;
}

interface Props {
  header: string[];
  headerResponsive?: string[];
  data: any;
  children: React.ReactNode;
  right?: React.ReactNode;
  title?: string;
  className?: string;
  center?: number[];
  centerResponsive?: number[];
  rightAlign?: number[];
  rightAlignResponsive?: number[];
  titleRight?: React.ReactNode;
  noItemsMessage?: string;
  showAlwaysHeader?: boolean;
  headerContent?: React.ReactNode;
  sortableColumns?: SortableColumn[];
  rawData?: any[];
  onSort?: (sortedData: any[]) => void;
  serverSort?: boolean;
  onServerSort?: (key: string, direction: SortDirection) => void;
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((acc, part) => {
    if (acc == null) return null;
    return acc[part];
  }, obj);
};

const Table = (props: Props) => {
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const isSortable = (index: number): SortableColumn | undefined => {
    return props.sortableColumns?.find((col) => col.index === index);
  };

  const handleSort = useCallback(
    (colConfig: SortableColumn) => {
      let newDirection: SortDirection;
      if (sortColumnIndex !== colConfig.index) {
        newDirection = 'asc';
      } else if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else {
        newDirection = null;
      }

      setSortColumnIndex(newDirection === null ? null : colConfig.index);
      setSortDirection(newDirection);

      if (props.serverSort) {
        // Orden delegado al backend: el padre es responsable de volver a
        // pedir los datos con el sort_by/sort_direction correspondientes.
        props.onServerSort?.(colConfig.key, newDirection);
        return;
      }

      if (!props.rawData || !props.onSort) return;

      if (newDirection === null) {
        props.onSort([...props.rawData]);
        return;
      }

      const sorted = [...props.rawData].sort((a, b) => {
        let valA: any;
        let valB: any;

        if (colConfig.accessor) {
          valA = colConfig.accessor(a);
          valB = colConfig.accessor(b);
        } else {
          valA = getNestedValue(a, colConfig.key);
          valB = getNestedValue(b, colConfig.key);
        }

        // Handle nulls/undefined — push them to the end
        if (valA == null && valB == null) return 0;
        if (valA == null) return 1;
        if (valB == null) return -1;

        let comparison = 0;

        switch (colConfig.type) {
          case 'string':
            comparison = String(valA).localeCompare(String(valB), 'es', {
              sensitivity: 'base',
            });
            break;
          case 'number':
            comparison = Number(valA) - Number(valB);
            break;
          case 'date':
            const dateA = moment(valA, colConfig.dateFormat);
            const dateB = moment(valB, colConfig.dateFormat);
            comparison = dateA.valueOf() - dateB.valueOf();
            break;
        }

        return newDirection === 'asc' ? comparison : -comparison;
      });

      props.onSort(sorted);
    },
    [
      props.rawData,
      props.onSort,
      props.serverSort,
      props.onServerSort,
      sortColumnIndex,
      sortDirection,
    ],
  );

  const renderSortIcon = (index: number) => {
    const colConfig = isSortable(index);
    if (!colConfig) return null;

    const isActive = sortColumnIndex === index;

    return (
      <span
        className={`sort-icon ${isActive ? 'sort-icon-active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          handleSort(colConfig);
        }}
      >
        {!isActive || sortDirection === null ? (
          <svg
            width='10'
            height='14'
            viewBox='0 0 10 14'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M5 0L9.33 5H0.67L5 0Z' opacity='0.4' />
            <path d='M5 14L0.67 9H9.33L5 14Z' opacity='0.4' />
          </svg>
        ) : sortDirection === 'asc' ? (
          <svg
            width='10'
            height='14'
            viewBox='0 0 10 14'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M5 0L9.33 5H0.67L5 0Z' opacity='1' />
            <path d='M5 14L0.67 9H9.33L5 14Z' opacity='0.25' />
          </svg>
        ) : (
          <svg
            width='10'
            height='14'
            viewBox='0 0 10 14'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M5 0L9.33 5H0.67L5 0Z' opacity='0.25' />
            <path d='M5 14L0.67 9H9.33L5 14Z' opacity='1' />
          </svg>
        )}
      </span>
    );
  };

  const renderHeaderCell = (label: string, index: number) => {
    const colConfig = isSortable(index);
    const alignClass =
      props.center && props.center.indexOf(index) != -1
        ? 'text-center'
        : props.rightAlign && props.rightAlign.indexOf(index) != -1
          ? 'text-right'
          : '';
    const justifyClass =
      props.center && props.center.indexOf(index) != -1
        ? 'justify-content-center'
        : props.rightAlign && props.rightAlign.indexOf(index) != -1
          ? 'justify-content-end'
          : '';

    return (
      <th
        className={`${alignClass} ${colConfig ? 'th-sortable' : ''}`}
        key={index}
        onClick={colConfig ? () => handleSort(colConfig) : undefined}
      >
        <div className={`container-blue-th ${justifyClass}`}>
          {label}
          {renderSortIcon(index)}
        </div>
      </th>
    );
  };

  let content = (
    <table className={`table table-component ${props.className}`}>
      <thead className='thead-normal'>
        <tr>{props.header.map((i: string, index: number) => renderHeaderCell(i, index))}</tr>
      </thead>
      <thead className='thead-responsive'>
        <tr>
          {props.headerResponsive?.map((i: string, index: number) => {
            return (
              <th
                className={
                  props.centerResponsive && props.centerResponsive.indexOf(index) != -1
                    ? 'text-center'
                    : props.rightAlignResponsive && props.rightAlignResponsive.indexOf(index) != -1
                      ? 'text-right'
                      : ''
                }
                key={index}
              >
                {i}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>{props.children}</tbody>
    </table>
  );

  if (!props.data || props.data == 0) {
    content = (
      <>
        {props.showAlwaysHeader && (
          <table className={`table table-component ${props.className}`}>
            <thead className='thead-normal'>
              <tr>{props.header.map((i: string, index: number) => renderHeaderCell(i, index))}</tr>
            </thead>
            <thead className='thead-responsive'>
              <tr>
                {props.headerResponsive?.map((i: string, index: number) => {
                  return (
                    <th
                      className={
                        props.centerResponsive && props.centerResponsive.indexOf(index) != -1
                          ? 'text-center'
                          : props.rightAlignResponsive &&
                              props.rightAlignResponsive.indexOf(index) != -1
                            ? 'text-right'
                            : ''
                      }
                      key={index}
                    >
                      {i}
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        )}
        <h2 className='no-table-data'>{props.noItemsMessage || 'No se encontraron registros'}</h2>
      </>
    );
  }

  return (
    <div className='table-container-white'>
      {props.headerContent}
      {(props.title || props.titleRight) && (
        <div className='row row-title-table'>
          <div className='col-md-7'>
            {props.title != null && <Title right={props.right} name={props.title || ''} />}
          </div>
          <div className='col-md-5 text-right'>
            <div className='container-title-right'>{props.titleRight}</div>
          </div>
        </div>
      )}
      <div className='table-container table-container-component'>{content}</div>
    </div>
  );
};

export type { SortableColumn, SortType, SortDirection };
export default Table;
